// Load each page's code only when it is first opened (React.lazy), and survive a deploy that happens while a page
// is open.
//
// WHY A DEPLOY CAN BREAK A LAZY PAGE. The files are named by a hash of their content. A deploy replaces them, so a
// browser tab that was opened before the deploy still points at the OLD names; opening a new page asks for a file
// that no longer exists. That used to be a blank screen. Now:
//   1. the first time a page file cannot be loaded, the site reloads ONCE, which fetches the new index.html and the
//      new file names, and the user lands on the page they asked for;
//   2. if that does not help (reloaded within the last 30 seconds, or the browser will not let us remember that we
//      reloaded), a clear message with a Reload button is shown instead of a blank page. It never reloads in a loop.
// Only a timestamp is kept in sessionStorage for this (no customer data).
import { Component, lazy, type ComponentType, type ReactNode } from 'react';

const RELOAD_KEY = 'floxir.chunkReloadAt';
const RELOAD_WINDOW_MS = 30000;

// Does this error mean "a page file could not be loaded"?
export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  const text = `${e?.name || ''} ${e?.message || ''}`;
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(text);
}

// Reload once. Returns true when a reload was started; false when we already reloaded a moment ago (or cannot
// remember that we did), so the caller must show the message instead.
export function reloadOnce(now: number = Date.now(), reload: () => void = () => window.location.reload()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false; // cannot remember: do not risk a reload loop
  }
  reload();
  return true;
}

export function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>, tryReload: () => boolean = () => reloadOnce()) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && tryReload()) {
        // the page is reloading: wait for it (never resolves) instead of flashing an error
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// For pages that export several screens from one file.
export function lazyNamed<M extends Record<string, any>, K extends keyof M>(factory: () => Promise<M>, name: K) {
  return lazyWithRetry<M[K]>(() => factory().then((m) => ({ default: m[name] })));
}

type State = { failed: boolean };

// Shows a clear message (never a blank page) when a page file cannot be loaded or a page crashes while loading.
export class PageErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 440, textAlign: 'center', fontSize: 14, lineHeight: 1.6 }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>This page could not be loaded</div>
          <div style={{ marginBottom: 20 }}>
            Floxir was probably updated while this window was open. Reload to get the latest version.
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#E8590C', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
